import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Compass, Sparkles, AlertCircle, ChevronDown, ChevronRight,
  BookOpen, PenLine, GraduationCap, ArrowRight, Info, CheckCircle2,
} from "lucide-react";

const CONFIDENCE_CHIP = {
  stronger: { label: "stronger evidence", cls: "border-progress-teal/40 text-progress-teal bg-progress-teal/10" },
  moderate: { label: "moderate evidence", cls: "border-accent-warm/40 text-accent-warm bg-accent-warm/10" },
  limited:  { label: "limited evidence",  cls: "border-fg-secondary/40 text-fg-secondary bg-bg-surface-hover" },
};

export default function Briefing() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/briefing");
        setData(data);
      } catch (err) {
        setError(extractApiError(err, "Couldn't load your briefing."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div data-testid="briefing-loading" className="mx-auto max-w-3xl px-4 py-16 text-center text-fg-secondary">
          Preparing your Career Repath Assessment briefing…
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell>
        <div className="mx-auto max-w-3xl px-4 py-14">
          <Alert variant="destructive" data-testid="briefing-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </AppShell>
    );
  }

  if (!data?.assessment_submitted) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-14">
          <div data-testid="briefing-not-submitted" className="rounded-lg border border-border-subtle bg-bg-surface p-8">
            <h1 className="text-3xl font-heading font-medium text-fg-primary">Not ready yet</h1>
            <p className="mt-3 text-sm text-fg-secondary">
              Your Career Repath Assessment briefing opens after you submit your assessment. You can save and revise anytime.
            </p>
            <div className="mt-5 flex gap-3">
              <Link to="/assessment"><Button data-testid="briefing-open-assessment" className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover">Open the assessment</Button></Link>
              <Link to="/dashboard"><Button variant="ghost" data-testid="briefing-back-dashboard">Dashboard</Button></Link>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  const directions = data.direction_previews || [];
  const narrative = data.narrative || {};
  const uncertainties = data.uncertainties || { has_uncertainties: false, count: 0, items: [] };
  const mapping = data.pathway_mapping;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-wider text-fg-secondary" data-testid="briefing-eyebrow">Your Career Repath Assessment — briefing</p>
          <h1 data-testid="briefing-title" className="mt-1 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-heading font-medium text-fg-primary">
            Thank you for the work you put in.
          </h1>
          <p data-testid="briefing-effort-ack" className="mt-3 max-w-2xl text-sm text-fg-secondary">
            You answered honestly and in enough depth that we could look for patterns.
            What follows is a draft picture <span className="text-fg-primary">you</span> control — nothing here is a decision, a ranking of your worth, or a promise about jobs.
          </p>
          {/* M1 (fix cycle 2026-09-25, Mark): honest "as of" label
              sourced from stored direction_set.created_at (backend
              exposes it as analysis_generated_at_short on /briefing).
              Absent → no chip. Never uses wall-clock. */}
          {data.analysis_generated_at_short && (
            <p
              data-testid="briefing-analysis-as-of"
              className="mt-3 text-xs text-fg-muted"
            >
              Based on your answers as of {data.analysis_generated_at_short}
            </p>
          )}
        </div>

        {/* M4 (fix cycle 2026-09-25, Mark): honest "answers changed
            since analysis" pill on briefing. Linked to /results
            (reconsideration UI lives there); briefing surface is
            read-only so viewing here never consumes an allowance. */}
        {data.edited_after_submit && data.directions_generated && (
          <div
            role="status"
            data-testid="briefing-edited-since-analysis-pill"
            className="mb-6 rounded-md border border-accent-warm/50 bg-accent-warm/10 p-3 flex items-start gap-2"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 text-accent-warm flex-shrink-0" aria-hidden="true" />
            <div className="text-xs text-fg-primary">
              <p className="font-medium">
                You've edited answers since this analysis.
              </p>
              <p className="mt-1 text-fg-secondary">
                Your previous direction set is still saved. Open Results to
                Reconsider — nothing regenerates on its own.
              </p>
              <Link to="/results#directions" data-testid="briefing-edited-pill-open-results-btn">
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 border-accent-warm/50 text-accent-warm hover:bg-accent-warm/10"
                >
                  Open Results to Reconsider <ChevronRight className="h-3 w-3 ml-1" aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* 2A.1 — honest uncertainty disclosure */}
        {uncertainties.has_uncertainties && (
          <section data-testid="briefing-uncertainties" className="mb-8 rounded-lg border border-border-subtle bg-bg-surface p-5">
            <div className="flex items-baseline gap-2">
              <AlertCircle className="h-4 w-4 text-fg-secondary" aria-hidden="true" />
              <p className="text-xs uppercase tracking-wider text-fg-secondary">Honest limits of this picture</p>
            </div>
            <p className="mt-2 text-sm text-fg-secondary">
              A few things weren't fully answered — the picture below acknowledges them explicitly rather than glossing over them.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-fg-primary">
              {uncertainties.items.map((u, i) => (
                <li key={u.id || i} data-testid={`briefing-uncertainty-${i}`}>
                  <span className="font-medium">{u.title}</span>
                  <span className="ml-1 text-fg-secondary">— {u.body}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 2A.2 — three peer directions preamble */}
        <section data-testid="briefing-peer-preamble" className="mb-8 rounded-lg border border-progress-teal/30 bg-progress-teal/5 p-5">
          <div className="flex items-baseline gap-2">
            <Compass className="h-4 w-4 text-progress-teal" aria-hidden="true" />
            <p className="text-xs uppercase tracking-wider text-progress-teal">3 evidence-supported Lanes of Direction</p>
          </div>
          <p className="mt-2 text-sm text-fg-primary">
            CRA3D found 3 Lanes of Direction your evidence supports —{" "}
            <span className="text-fg-primary font-medium">peers, not a ranking.</span>{" "}
            You get to decide which one (if any) to explore first. You can revisit this
            picture, reconsider, or edit any part.
          </p>
          {data.diversity_summary?.has_limitation && (
            <p data-testid="briefing-diversity-limitation-notice" className="mt-2 text-xs text-fg-secondary italic">
              One of the three is a labeled alternative — see its disclosure below.
            </p>
          )}
        </section>

        {/* 2A.4 — narrative from stored profile (deterministic) */}
        {(narrative.what_we_heard || (narrative.sections || []).some((s) => s.items.length > 0)) && (
          <section id="briefing-narrative" data-testid="briefing-narrative" className="mb-10">
            <h2 className="text-2xl font-heading font-medium text-fg-primary">What we heard, in short</h2>
            <p className="mt-1 text-xs text-fg-secondary">
              Assembled from your own answers — nothing is added by AI here.
            </p>
            {narrative.what_we_heard && (
              <div data-testid="briefing-what-we-heard" className="mt-3 rounded-lg border border-border-subtle bg-bg-surface p-5 text-sm text-fg-primary whitespace-pre-line">
                {narrative.what_we_heard}
              </div>
            )}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {(narrative.sections || []).map((sec) => (
                <div key={sec.id} data-testid={`briefing-narrative-${sec.id}`} className="rounded-lg border border-border-subtle bg-bg-surface p-4">
                  <p className="text-[11px] uppercase tracking-wider text-fg-secondary">{sec.title}</p>
                  {sec.items.length === 0 ? (
                    <p className="mt-2 text-xs text-fg-muted italic" data-testid={`briefing-narrative-${sec.id}-empty`}>
                      Nothing recorded yet.
                    </p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {sec.items.map((it) => (
                        <li key={it.id} data-testid={`briefing-narrative-${sec.id}-item-${it.id}`}>
                          <p className="text-sm text-fg-primary font-medium">{it.title}</p>
                          {it.confidence && (
                            <span
                              data-testid={`briefing-narrative-${sec.id}-item-${it.id}-confidence`}
                              className={"mt-1 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border " + (CONFIDENCE_CHIP[it.confidence]?.cls || "")}
                            >
                              {CONFIDENCE_CHIP[it.confidence]?.label || it.confidence}
                            </span>
                          )}
                          <p className="mt-1 text-xs text-fg-secondary">{it.body}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {!data.profile_generated && (
          <section data-testid="briefing-profile-missing" className="mb-10 rounded-lg border border-border-subtle bg-bg-surface p-5">
            <p className="text-xs uppercase tracking-wider text-fg-secondary">Profile not generated yet</p>
            <p className="mt-2 text-sm text-fg-secondary">
              Your profile narrative isn't available here yet — you can generate it on the Results page.
            </p>
            <Link to="/results"><Button size="sm" data-testid="briefing-goto-results-for-profile" className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover">Open Results to generate</Button></Link>
          </section>
        )}

        {/* 2A.3 + 2A.5 + 2A.6 — direction previews */}
        <section id="briefing-directions" className="mb-10">
          <h2 className="text-2xl font-heading font-medium text-fg-primary" data-testid="briefing-directions-heading">
            3 peer Lanes of Direction
          </h2>
          <p className="mt-1 text-xs text-fg-secondary">
            Each carries evidence links, honest tradeoffs, and a preview of what you already bring vs. what it would require.
          </p>
          {directions.length === 0 ? (
            <div data-testid="briefing-directions-empty" className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-5">
              <p className="text-sm text-fg-secondary">
                Directions haven't been generated yet. Open Results to generate them.
              </p>
              <Link to="/results"><Button size="sm" data-testid="briefing-goto-results-for-directions" className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover">Open Results</Button></Link>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {directions.map((d) => (
                <BriefingDirection key={d.id} d={d} />
              ))}
            </div>
          )}
        </section>

        {/* 2A.7 handled inside BriefingDirection (external requirements clearly labeled) */}

        {/* 2A + mission statement (owner-verbatim) */}
        <section data-testid="briefing-mission" className="mb-10 rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5">
          <p className="text-xs uppercase tracking-wider text-accent-warm">CRA3D's promise</p>
          <p data-testid="briefing-mission-text" className="mt-2 text-base text-fg-primary leading-relaxed">
            {data.mission_statement}
          </p>
        </section>

        {/* 2A.8 — participant-controlled choices */}
        <section data-testid="briefing-cta-row" className="mb-16">
          <h2 className="text-2xl font-heading font-medium text-fg-primary">Where to next?</h2>
          <p className="mt-1 text-xs text-fg-secondary">
            Choose what fits your energy right now. Nothing here locks anything in.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Link to="/results#directions">
              <Button data-testid="briefing-cta-explore" variant="outline" className="w-full h-auto py-4 px-4 border-border-subtle text-fg-primary hover:bg-bg-surface-hover flex items-start gap-3">
                <Compass className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-left">
                  <span className="block font-medium">Explore directions</span>
                  <span className="block mt-0.5 text-xs text-fg-secondary">Read the full details, compare, then decide.</span>
                </span>
              </Button>
            </Link>
            <Link to="/results#directions">
              <Button data-testid="briefing-cta-compare-gaps" variant="outline" className="w-full h-auto py-4 px-4 border-border-subtle text-fg-primary hover:bg-bg-surface-hover flex items-start gap-3">
                <BookOpen className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-left">
                  <span className="block font-medium">Compare skill gaps</span>
                  <span className="block mt-0.5 text-xs text-fg-secondary">See what you have vs. what each direction requires.</span>
                </span>
              </Button>
            </Link>
            <Link to="/learning">
              <Button data-testid="briefing-cta-pathway" variant="outline" className="w-full h-auto py-4 px-4 border-border-subtle text-fg-primary hover:bg-bg-surface-hover flex items-start gap-3">
                <GraduationCap className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-left">
                  <span className="block font-medium">Choose a pathway to investigate</span>
                  <span className="block mt-0.5 text-xs text-fg-secondary">Look at the three practical learning routes.</span>
                </span>
              </Button>
            </Link>
            <Link to="/results#profile">
              <Button data-testid="briefing-cta-correct" variant="outline" className="w-full h-auto py-4 px-4 border-border-subtle text-fg-primary hover:bg-bg-surface-hover flex items-start gap-3">
                <PenLine className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-left">
                  <span className="block font-medium">Correct my results</span>
                  <span className="block mt-0.5 text-xs text-fg-secondary">Edit or reject any conclusion that isn't right.</span>
                </span>
              </Button>
            </Link>
            <Link to="/results">
              <Button data-testid="briefing-cta-continue" className="w-full h-auto py-4 px-4 bg-accent-warm text-bg-base hover:bg-accent-warm-hover flex items-start gap-3">
                <ArrowRight className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span className="text-left">
                  <span className="block font-medium">Continue to detailed Results</span>
                  <span className="block mt-0.5 text-xs text-bg-base/80">Full profile workspace, all edits.</span>
                </span>
              </Button>
            </Link>
          </div>
        </section>

        {/* Selected-direction pathway mapping surface (compact) */}
        {mapping && (
          <section data-testid="briefing-mapping-surface" className="mb-10 rounded-lg border border-progress-teal/30 bg-progress-teal/5 p-5">
            <p className="text-xs uppercase tracking-wider text-progress-teal">
              Strong learning match for your selected direction
            </p>
            <p data-testid="briefing-mapping-target" className="mt-1 text-sm text-fg-primary">
              Based on your confirmed Lane of Direction, one pathway matches your evidence most closely.
              You can compare, correct, or choose any pathway.
            </p>
            <Link to="/learning">
              <Button size="sm" data-testid="briefing-open-learning" className="mt-3 bg-accent-warm text-bg-base hover:bg-accent-warm-hover">
                Open the learning workspace <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
              </Button>
            </Link>
          </section>
        )}

        <div className="mt-8 text-center">
          <p data-testid="briefing-honest-footnote" className="text-xs text-fg-muted italic max-w-xl mx-auto">
            No employment, income, or hiring outcomes are guaranteed. AI conclusions are drafts.
            Nothing here reads or replaces your judgment.
          </p>
        </div>
      </div>
    </AppShell>
  );
}


function BriefingDirection({ d }) {
  const [open, setOpen] = useState(false);
  const sg = d.skill_gap_preview || {};
  const counts = sg.counts || { already_have: 0, direction_requires: 0, gaps: 0, external_requirements: 0 };
  const teaches = sg.cra3d_teaches_summary || { taught_by_cra3d: 0, not_currently_taught: 0 };
  const priority = sg.priority_summary || {};
  const isOther = d.category === "other";
  return (
    <div
      data-testid={`briefing-direction-${d.id}`}
      data-category={d.category}
      data-selected={d.is_selected}
      className={
        "rounded-lg border p-5 flex flex-col " +
        (d.is_selected
          ? "border-accent-warm bg-bg-surface"
          : "border-border-subtle bg-bg-surface")
      }
    >
      <div className="flex flex-wrap items-start gap-2">
        <span
          data-testid={`briefing-direction-${d.id}-chip`}
          className={
            "text-[11px] uppercase tracking-wider px-2 py-1 rounded border " +
            (isOther
              ? "border-border-subtle text-fg-muted bg-bg-base"
              : "border-progress-teal/40 text-progress-teal bg-progress-teal/10")
          }
        >
          {d.category_chip}
        </span>
        {d.is_selected && (
          <span
            data-testid={`briefing-direction-${d.id}-selected-badge`}
            className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-accent-warm text-accent-warm bg-accent-warm/10 inline-flex items-center gap-1"
          >
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            Your confirmed choice
          </span>
        )}
      </div>

      <h3 data-testid={`briefing-direction-${d.id}-title`} className="mt-3 text-lg font-heading font-medium text-fg-primary">
        {d.title}
      </h3>
      <p data-testid={`briefing-direction-${d.id}-description`} className="mt-1 text-sm text-fg-secondary">
        {d.description}
      </p>

      {d.category_limitation && (
        <div
          data-testid={`briefing-direction-${d.id}-limitation`}
          className="mt-3 rounded-md border border-fg-muted/40 bg-bg-base p-3"
        >
          <p className="text-[11px] uppercase tracking-wider text-fg-secondary flex items-center gap-1">
            <Info className="h-3 w-3" aria-hidden="true" />
            Category disclosure
          </p>
          <p className="mt-1 text-xs text-fg-secondary italic">{d.category_limitation}</p>
        </div>
      )}

      {/* Skill-gap preview (2A.6) */}
      <div data-testid={`briefing-direction-${d.id}-sg-preview`} className="mt-4 rounded-md border border-border-subtle bg-bg-base p-3">
        <p className="text-[11px] uppercase tracking-wider text-fg-secondary">Skill-gap preview</p>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div>
            <p className="text-fg-primary font-medium">You already bring</p>
            <p data-testid={`briefing-direction-${d.id}-sg-have`} className="text-fg-secondary">{counts.already_have} item(s)</p>
          </div>
          <div>
            <p className="text-fg-primary font-medium">The direction requires</p>
            <p data-testid={`briefing-direction-${d.id}-sg-req`} className="text-fg-secondary">{counts.direction_requires} item(s)</p>
          </div>
          <div>
            <p className="text-fg-primary font-medium">Honest gap</p>
            <p data-testid={`briefing-direction-${d.id}-sg-gap`} className="text-fg-secondary">
              {counts.gaps} item(s){" "}
              <span className="text-fg-muted italic">
                (CRA3D can help with {teaches.taught_by_cra3d}; {teaches.not_currently_taught} not currently taught)
              </span>
            </p>
          </div>
          <div>
            <p className="text-fg-primary font-medium">External requirements</p>
            <p data-testid={`briefing-direction-${d.id}-sg-ext`} className="text-fg-secondary">
              {counts.external_requirements} item(s){" "}
              <span className="text-fg-muted italic">(CRA3D cannot provide)</span>
            </p>
          </div>
        </div>
      </div>

      <Collapsible open={open} onOpenChange={setOpen} className="mt-4">
        <CollapsibleTrigger asChild>
          <button
            data-testid={`briefing-direction-${d.id}-expand-evidence`}
            className="flex items-center gap-2 text-xs text-fg-secondary hover:text-fg-primary transition-colors"
          >
            <ChevronDown className={"h-3 w-3 transition-transform " + (open ? "rotate-180" : "")} aria-hidden="true" />
            Why this may fit your evidence ({(d.supporting_answers || []).length} answers)
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <p className="text-xs text-fg-secondary" data-testid={`briefing-direction-${d.id}-why`}>{d.why_it_may_fit}</p>
          {d.practical_first_step && (
            <div className="mt-3 rounded-md border border-progress-teal/30 bg-progress-teal/5 p-3">
              <p className="text-[11px] uppercase tracking-wider text-progress-teal">Practical first step</p>
              <p data-testid={`briefing-direction-${d.id}-first-step`} className="mt-1 text-xs text-fg-primary">
                {d.practical_first_step}
              </p>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Link to={`/results#direction-${d.id}`} className="mt-4">
        <Button
          size="sm"
          variant="outline"
          data-testid={`briefing-direction-${d.id}-open`}
          className="w-full border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          Open full details <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
        </Button>
      </Link>
    </div>
  );
}
